import { useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface JourneyOptionItem {
  id: string;
  label: string;
}

interface PickYourJourneyProps {
  options?: JourneyOptionItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

export const PickYourJourney = ({
  options,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: PickYourJourneyProps) => {
  const { t } = useTranslation();
  const defaultJourneyOptions = useMemo<JourneyOptionItem[]>(
    () => [
      { id: "beginner", label: t("pickYourJourney.optionBeginner") },
      { id: "intermediate", label: t("pickYourJourney.optionIntermediate") },
      { id: "advanced", label: t("pickYourJourney.optionAdvanced") },
    ],
    [t],
  );
  const resolvedOptions = options ?? defaultJourneyOptions;
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("pickYourJourney.skipForNow");
  const [selected, setSelected] = useState<JourneyOptionItem | null>(
    initialSelectedId ? (resolvedOptions.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const handleSelectionChange = (item: JourneyOptionItem | null) => {
    setSelected(item);
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue?.(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={!selected}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
      contentClassName="pb-6 pt-[46px]"
    >
      <PersonalisationHeading size="sm">{t("pickYourJourney.heading")}</PersonalisationHeading>

      <SingleChoiceTagPicker
        items={resolvedOptions}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel={t("pickYourJourney.ariaLabel")}
      />
    </PersonalisationScreenLayout>
  );
};
